import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';

import type { LineAreaCapabilities, LineAreaMeasure, LineAreaVariant } from '../../../chart-components/unified-line-area-chart/unified-line-area-chart.types';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import { isServerComputedClientTile } from '../../dvm-layout-builder/dvm-builder-client-chart.utils';
import type { DashboardTile, LineAreaBuilderMeasure } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { LabelValueOption, LineAreaVariantOption } from '../../shared/self-service-config.types';
import { buildLineAreaComputeWidgetData, extractLineAreaComputeRows, extractLineAreaFlatRows, lineAreaRowsFitCapabilities } from '../../shared/self-service-recompute.utils';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { applyLineAreaFormatToCapabilities, remapLineAreaCapabilitiesToDisplayNames } from './line-area-capabilities.utils';

/**
 * Line & Area build form (LINE_AREA): variant picker (line / multi-line /
 * step / spline / area / stacked / 100%), category + series + secondary
 * axes, N measures with colour / symbol, reference lines, and the shared
 * Format accordion. Renders client-side from the cached dataset rows (live
 * preview tile); Generate also POSTs for server-computed rows.
 */
@Component({
  selector: 'app-line-area-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule],
  templateUrl: './line-area-step.component.html',
  styles: [':host { display: contents; }'],
})
export class LineAreaStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['LINE_AREA'] as const;
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);

  laVariant: LineAreaVariant = 'line';
  laCategory = '';
  laCategoryParent = '';
  laCategoryLabel = '';
  laYAxisLabel = '';
  laSecondaryYLabel = '';
  laSecondaryXLabel = '';
  laXAxisFontWeight = '';
  laYAxisFontWeight = '';
  laSecondaryXFontWeight = '';
  laSecondaryYFontWeight = '';
  laSeries = '';
  laReferenceLines: Array<{ id: string; value: number | null; label: string; color: string }> = [];
  private laRefLineIdCounter = 0;
  laMeasures: LineAreaBuilderMeasure[] = [];
  laSecondaryYField = '';
  laSecondaryXField = '';
  laSecondaryXValueField = '';
  laSecondaryXPosition: 'top' | 'bottom' = 'bottom';
  readonly laSecondaryXPositions: Array<{ label: string; value: 'top' | 'bottom' }> = [
    { label: 'Below primary X-axis', value: 'bottom' },
    { label: 'Top of chart', value: 'top' },
  ];
  private laMeasureIdCounter = 0;

  private readonly lineAreaVariantsFallback: LineAreaVariantOption[] = [
    { label: 'Line', value: 'line', group: 'Line', usesSeries: false, allowMultipleMeasures: true },
    { label: 'Multi-Line', value: 'multi-line', group: 'Line', usesSeries: true },
    { label: 'Step Line', value: 'step', group: 'Line', usesSeries: false, allowMultipleMeasures: true, step: true },
    { label: 'Spline Line', value: 'spline', group: 'Line', usesSeries: false, allowMultipleMeasures: true, smooth: true },
    { label: 'Area', value: 'area', group: 'Area', usesSeries: false, allowMultipleMeasures: true, area: true },
    { label: 'Stacked Area', value: 'stacked-area', group: 'Area', usesSeries: true, area: true, stack: true },
    { label: '100% Stacked Area', value: 'area-100', group: 'Area', usesSeries: true, area: true, stack: true, normalize100: true },
    { label: 'Spline Area', value: 'spline-area', group: 'Area', usesSeries: false, allowMultipleMeasures: true, area: true, smooth: true },
  ];
  get lineAreaVariants(): LineAreaVariantOption[] {
    return this.host.layout.options?.lineAreaVariants?.length
      ? (this.host.layout.options.lineAreaVariants as LineAreaVariantOption[])
      : this.lineAreaVariantsFallback;
  }
  private get laVariantEntry(): LineAreaVariantOption | undefined {
    return this.lineAreaVariants.find((v) => v.value === this.laVariant);
  }
  private readonly lineAreaRenderTypesFallback: LabelValueOption[] = [
    { label: 'Line', value: 'line' }, { label: 'Area', value: 'area' },
  ];
  get lineAreaRenderTypes(): LabelValueOption[] {
    return this.host.layout.options?.lineAreaRenderTypes?.length ? this.host.layout.options.lineAreaRenderTypes : this.lineAreaRenderTypesFallback;
  }
  private readonly lineAreaAxisOptionsFallback: LabelValueOption[] = [
    { label: 'Primary axis', value: 'y1' }, { label: 'Secondary axis (dual)', value: 'y2' },
  ];
  get lineAreaAxisOptions(): LabelValueOption[] {
    return this.host.layout.options?.lineAreaAxisOptions?.length ? this.host.layout.options.lineAreaAxisOptions : this.lineAreaAxisOptionsFallback;
  }
  private readonly lineAreaSymbolOptionsFallback: LabelValueOption[] = [
    { label: 'None', value: 'none' },
    { label: 'Circle', value: 'circle' },
    { label: 'Square', value: 'rect' },
    { label: 'Diamond', value: 'diamond' },
    { label: 'Triangle', value: 'triangle' },
    { label: 'Pin', value: 'pin' },
    { label: 'Arrow', value: 'arrow' },
  ];
  get lineAreaSymbolOptions(): LabelValueOption[] {
    return this.host.layout.options?.lineAreaSymbolOptions?.length ? this.host.layout.options.lineAreaSymbolOptions : this.lineAreaSymbolOptionsFallback;
  }

  get laVariantUsesSeries(): boolean {
    return this.laVariantEntry?.usesSeries === true;
  }
  get laSeriesRequired(): boolean {
    return this.laVariantUsesSeries && this.laMeasures.filter((measure) => !!measure.field).length < 2;
  }
  get laVariantHint(): string {
    return this.laVariantEntry?.hint || '';
  }
  get laAllowsMultipleMeasures(): boolean {
    if (this.laVariantEntry?.allowMultipleMeasures === true) return true;
    return !this.laVariantUsesSeries;
  }
  get laHasSecondaryY(): boolean {
    return !!this.laSecondaryYField.trim() || this.laMeasures.some((m) => !!m.field && m.axis === 'y2');
  }

  addLaMeasure(): void {
    this.laMeasures = [...this.laMeasures, { id: this.nextLaMeasureId(), field: '', label: '', renderAs: 'line', axis: 'y1', symbol: 'circle' }];
  }
  removeLaMeasure(measure: LineAreaBuilderMeasure): void {
    this.laMeasures = this.laMeasures.filter((item) => item.id !== measure.id);
    this.host.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  moveLaMeasureUp(index: number): void { if (index > 0) this.reorderLaMeasure(index, index - 1); }
  moveLaMeasureDown(index: number): void { if (index < this.laMeasures.length - 1) this.reorderLaMeasure(index, index + 1); }
  private reorderLaMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.laMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.laMeasures = next;
    this.host.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLaMeasureChange(): void {
    this.laMeasures = [...this.laMeasures];
    this.host.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLaMeasureSymbolChange(measure: LineAreaBuilderMeasure, symbol: string): void {
    measure.symbol = symbol;
    if (symbol !== 'none' && Number(this.host.barFormat['symbolSize']) <= 0) {
      this.host.barFormat = { ...this.host.barFormat, symbolSize: 6 };
    }
    this.onLaMeasureChange();
  }
  onLaMeasureSymbolSizeChange(measure: LineAreaBuilderMeasure, value: unknown): void {
    const parsed = value === '' || value === null || value === undefined ? 6 : Number(value);
    measure.symbolSize = Number.isFinite(parsed) ? Math.max(0, Math.min(24, parsed)) : 6;
    this.onLaMeasureChange();
  }
  laMeasureColor(measure: LineAreaBuilderMeasure, index: number): string {
    const explicit = String(measure?.color ?? '').trim();
    if (explicit) return explicit;
    const palette = (this.host.layout.options?.lineAreaChartDefaults?.['palette'] as string[] | undefined) || [];
    return palette.length ? palette[index % palette.length] : '#5470c6';
  }
  onLineAreaChange(): void {
    this.host.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  private nextLaMeasureId(): string {
    this.laMeasureIdCounter += 1;
    return `la-measure-${this.laMeasureIdCounter}`;
  }
  onLaMeasureFieldDrop(event: DragEvent, measure?: LineAreaBuilderMeasure): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    const target = measure || { id: this.nextLaMeasureId(), field: '', label: '', renderAs: 'line' as const, axis: 'y1' as const, symbol: 'circle' };
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!measure) this.laMeasures = [...this.laMeasures, target];
    this.onLaMeasureChange();
  }
  onLaCategoryFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.laCategory = mapping.field;
    if (!this.laCategoryLabel) this.laCategoryLabel = mapping.field;
    this.onLineAreaChange();
  }
  onLaSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.laSeries = mapping.field;
    this.onLineAreaChange();
  }
  onLaSecondaryYFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.laSecondaryYField = mapping.field;
    this.onLineAreaChange();
  }
  onLaSecondaryXFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.laSecondaryXField = mapping.field;
    this.onLineAreaChange();
  }
  addLaReferenceLine(): void {
    this.laRefLineIdCounter += 1;
    this.laReferenceLines = [...this.laReferenceLines, { id: `la-refline-${this.laRefLineIdCounter}`, value: null, label: '', color: '#64748b' }];
  }
  removeLaReferenceLine(id: string): void {
    this.laReferenceLines = this.laReferenceLines.filter((line) => line.id !== id);
    this.host.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLaReferenceLineChange(): void {
    this.laReferenceLines = [...this.laReferenceLines];
    this.host.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }

  refreshEditingLineAreaTile(): void {
    if (!this.active) return;
    if (this.host.editingTileId) {
      const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
      const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'LINE_AREA') {
        this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? this.rebuildLineAreaTile(tile) : t));
      }
      return;
    }
    if (!this.canPreviewLineArea) {
      this.host.removePreviewTile();
      return;
    }
    const existingIndex = this.host.previewTileId ? this.host.dashboardTiles.findIndex((t) => t.id === this.host.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.host.newTileBase(this.host.buildPayload());
      this.host.previewTileId = base.id;
      const draft = this.rebuildLineAreaTile({ ...base, layoutType: 'LINE_AREA' });
      this.host.dashboardTiles = [...this.host.dashboardTiles, draft];
    } else {
      const tile = this.host.dashboardTiles[existingIndex];
      this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildLineAreaTile(tile) : t));
    }
  }

  private applyLineAreaComputeResponse(componentId: string, response: Record<string, unknown> | null): void {
    const rows = extractLineAreaComputeRows(response?.['responseList'])
      ?? extractLineAreaComputeRows(response)
      ?? extractLineAreaFlatRows(response?.['responseList'])
      ?? extractLineAreaFlatRows(response);
    if (!rows?.length || !componentId) return;
    const index = this.host.dashboardTiles.findIndex((t) => t.componentId === componentId && t.layoutType === 'LINE_AREA');
    if (index < 0) return;
    const tile = this.host.dashboardTiles[index];
    if (!lineAreaRowsFitCapabilities(rows, tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined)) return;
    const data = buildLineAreaComputeWidgetData(response, {
      widgetId: 'dvm-tile-line-area-' + tile.id,
      title: tile.title,
      capabilities: (tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined),
      format: tile.format,
    });
    this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? { ...t, lineAreaRows: null, lineAreaData: data } : t));
    this.host.reflowCharts();
  }

  private rebuildLineAreaTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.host.defaultTileFormat('LINE_AREA'), ...this.host.barFormat, title: this.host.title.trim() || undefined };
    const existing = tile.lineAreaData as Record<string, unknown> | null | undefined;
    if (existing && isServerComputedClientTile(existing, this.laCategory)) {
      const existingCaps: LineAreaCapabilities = {
        ...(existing['capabilities'] as LineAreaCapabilities),
        categoryLabel: this.laCategoryLabel || this.laCategory || (existing['capabilities'] as LineAreaCapabilities)?.categoryLabel,
        yAxisLabel: this.laYAxisLabel.trim() || undefined,
        yAxisLabel2: this.laSecondaryYLabel.trim() || undefined,
        xAxisLabel2: this.laSecondaryXLabel.trim() || undefined,
      };
      const currentMeasures = this.buildLineAreaCapabilities().measures;
      if (Array.isArray(existingCaps.measures) && currentMeasures.length) {
        existingCaps.measures = existingCaps.measures.map((measure, index) => ({
          ...measure,
          ...(currentMeasures[index]?.symbol ? { symbol: currentMeasures[index].symbol } : {}),
          ...(Number.isFinite(Number(currentMeasures[index]?.symbolSize))
            ? { symbolSize: Number(currentMeasures[index]?.symbolSize) }
            : {}),
        }));
      }
      const caps = this.applyLineAreaFormatToCapabilities(existingCaps, format);
      if (!this.laSecondaryXField.trim()) {
        delete caps.secondaryXField;
        delete caps.secondaryXValueField;
        delete caps.secondaryXPosition;
      } else {
        caps.secondaryXPosition = this.laSecondaryXPosition;
      }
      return {
        ...tile,
        title: this.host.title.trim() || tile.title || 'Untitled',
        format,
        lineAreaData: { ...existing, title: this.host.title.trim() || tile.title, capabilities: caps },
      };
    }
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    return {
      ...tile,
      title: this.host.title.trim() || tile.title || 'Untitled',
      format,
      lineAreaRows: rows,
      lineAreaData: this.buildLineAreaWidgetData(tile.id, format),
    };
  }

  get canPreviewLineArea(): boolean {
    if (!this.active || !this.host.drillDataset) return false;
    if (!this.host.cachedRows(this.host.drillDataset)?.length) return false;
    const filledMeasures = this.laMeasures.filter((m) => !!m.field);
    if (!this.laCategory || !filledMeasures.length) return false;
    if (this.laVariantUsesSeries && !this.laSeries && filledMeasures.length < 2) return false;
    return true;
  }

  private buildLineAreaCapabilities(): LineAreaCapabilities {
    const measures: LineAreaMeasure[] = this.laMeasures
      .filter((measure) => measure.field)
      .map((measure) => ({
        field: measure.field,
        label: measure.label || measure.field,
        renderAs: measure.renderAs,
        axis: measure.axis,
        ...(String(measure.color ?? '').trim() ? { color: measure.color } : {}),
                      ...(String(measure.symbol ?? '').trim() ? { symbol: measure.symbol } : {}),
        ...(Number.isFinite(Number(measure.symbolSize)) ? { symbolSize: Number(measure.symbolSize) } : {}),
      }));
    const secondaryField = this.laSecondaryYField.trim();
    if (secondaryField && !measures.some((m) => m.field === secondaryField)) {
      measures.push({ field: secondaryField, label: secondaryField, renderAs: 'line', axis: 'y2' });
    }
    const referenceLines = this.laReferenceLines
      .filter((line) => line.value !== null && Number.isFinite(Number(line.value)))
      .map((line) => ({ value: Number(line.value), label: line.label || undefined, color: line.color || undefined }));
    return {
      variant: this.laVariant,
      category: this.laCategory,
      categoryParent: this.laCategoryParent && this.laCategoryParent !== this.laCategory ? this.laCategoryParent : undefined,
      categoryLabel: this.laCategoryLabel || this.laCategory,
      series: this.laVariantUsesSeries && this.laSeries ? this.laSeries : undefined,
      measures,
      ...(this.laYAxisLabel.trim() ? { yAxisLabel: this.laYAxisLabel.trim() } : {}),
      ...(this.laSecondaryYLabel.trim() ? { yAxisLabel2: this.laSecondaryYLabel.trim() } : {}),
      ...(this.laXAxisFontWeight ? { xAxisFontWeight: this.laXAxisFontWeight } : {}),
      ...(this.laYAxisFontWeight ? { yAxisFontWeight: this.laYAxisFontWeight } : {}),
      ...(this.laSecondaryXFontWeight ? { xAxis2FontWeight: this.laSecondaryXFontWeight } : {}),
      ...(this.laSecondaryYFontWeight ? { yAxis2FontWeight: this.laSecondaryYFontWeight } : {}),
      ...(this.laSecondaryXField.trim()
        ? {
            secondaryXField: this.laSecondaryXField.trim(),
            secondaryXValueField: (this.laSecondaryXValueField.trim() || this.laMeasures.find((m) => m.field)?.field || ''),
            secondaryXPosition: this.laSecondaryXPosition,
            ...(this.laSecondaryXLabel.trim() ? { xAxisLabel2: this.laSecondaryXLabel.trim() } : {}),
          }
        : {}),
      ...(referenceLines.length ? { referenceLines } : {}),
      ...(this.host.layout.options?.lineAreaChartDefaults ? { chartDefaults: this.host.layout.options.lineAreaChartDefaults } : {}),
      variantFlags: {
        step: this.laVariantEntry?.step === true,
        smooth: this.laVariantEntry?.smooth === true,
        area: this.laVariantEntry?.area === true,
        stack: this.laVariantEntry?.stack === true,
        normalize100: this.laVariantEntry?.normalize100 === true,
      },
    } as LineAreaCapabilities;
  }

  private applyLineAreaFormatToCapabilities(capabilities: LineAreaCapabilities, format?: Record<string, unknown>): LineAreaCapabilities {
    return applyLineAreaFormatToCapabilities(capabilities, format, (key) => this.host.resolvePaletteColors(key));
  }

  private buildLineAreaColumns(capabilities: LineAreaCapabilities): Array<Record<string, unknown>> {
    const dataset = this.host.drillDataset;
    const columns: Array<Record<string, unknown>> = [{
      dataset,
      field: this.laCategory,
      displayName: this.laCategoryLabel || this.laCategory,
    }];
    for (const measure of capabilities.measures || []) {
      if (!measure.field) continue;
      columns.push({
        dataset,
        field: measure.field,
        aggregation: 'SUM',
        displayName: measure.label || measure.field,
      });
    }
    const sortingFields = (capabilities.sorting || []).map((entry) => String(entry.field || '').trim()).filter(Boolean);
    const extras = [capabilities.series, capabilities.secondaryXField, ...sortingFields].map((field) => (field || '').trim()).filter(Boolean);
    for (const field of extras) {
      if (!columns.some((column) => column['field'] === field)) {
        columns.push({ dataset, field, displayName: field });
      }
    }
    return columns;
  }

  private buildLineAreaTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.host.newTileBase(payload);
    base.format = { ...this.host.defaultTileFormat('LINE_AREA'), ...this.host.barFormat, title: this.host.title.trim() || undefined };
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    return { ...base, lineAreaRows: rows, lineAreaData: this.buildLineAreaWidgetData(base.id, base.format) };
  }

  private buildLineAreaWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    const capabilities = this.applyLineAreaFormatToCapabilities(this.buildLineAreaCapabilities(), format);
    return {
      widgetId: 'dvm-tile-line-area-' + tileId,
      title: this.host.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    const filledMeasures = this.laMeasures.filter((measure) => !!measure.field);
    if (!this.laCategory) missing.push('Category');
    if (!filledMeasures.length) missing.push('At least one measure');
    if (this.laVariantUsesSeries && !this.laSeries && filledMeasures.length < 2) {
      missing.push('Series column (or a second measure)');
    }
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const capabilities = this.applyLineAreaFormatToCapabilities(this.buildLineAreaCapabilities(), this.host.barFormat);
    const sorting = capabilities.sorting?.length ? capabilities.sorting : undefined;
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: this.host.selectedLayoutType, // 'LINE_AREA'
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      columns: this.buildLineAreaColumns(capabilities),
      ...(sorting ? { sorting } : {}),
      capabilities: remapLineAreaCapabilitiesToDisplayNames(capabilities) as unknown as Record<string, unknown>,
      format: { ...this.host.defaultTileFormat('LINE_AREA'), ...this.host.barFormat, title: this.host.title.trim() || undefined },
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
      ?? ((tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined))
      ?? {};
    const savedColumns = Array.isArray(payload['columns']) ? payload['columns'] as Array<Record<string, unknown>> : [];
    const rawFieldByDisplayName = new Map(savedColumns
      .map((column) => [String(column['displayName'] ?? column['field'] ?? ''), String(column['field'] ?? '')] as const)
      .filter(([displayName, field]) => displayName && field));
    const toRawField = (name: string) => rawFieldByDisplayName.get(name) || name;
    this.laVariant = (String(caps['variant'] ?? 'line') as LineAreaVariant);
    this.laCategory = toRawField(String(caps['category'] ?? ''));
    this.laCategoryParent = String(caps['categoryParent'] ?? '');
    this.laCategoryLabel = String(caps['categoryLabel'] ?? '');
    const laSavedFormat = (tile.format || {}) as Record<string, unknown>;
    this.laYAxisLabel = laSavedFormat['yAxisLabel'] ? '' : String(caps['yAxisLabel'] ?? '');
    this.laSecondaryYLabel = laSavedFormat['yAxisLabel2'] ? '' : String(caps['yAxisLabel2'] ?? '');
    this.laSecondaryXLabel = laSavedFormat['xAxisLabel2'] ? '' : String(caps['xAxisLabel2'] ?? '');
    this.laXAxisFontWeight = String(caps['xAxisFontWeight'] ?? '');
    this.laYAxisFontWeight = String(caps['yAxisFontWeight'] ?? '');
    this.laSecondaryXFontWeight = String(caps['xAxis2FontWeight'] ?? '');
    this.laSecondaryYFontWeight = String(caps['yAxis2FontWeight'] ?? '');
    this.laSeries = String(caps['series'] ?? '');
    const savedMeasures = Array.isArray(caps['measures']) ? caps['measures'] as Array<Record<string, unknown>> : [];
    this.laMeasures = savedMeasures.length
      ? savedMeasures.map((m) => ({
          id: this.nextLaMeasureId(),
          field: toRawField(String(m['field'] ?? '')),
          label: String(m['label'] ?? ''),
          renderAs: (String(m['renderAs'] ?? 'line') as 'line' | 'area'),
          axis: (String(m['axis'] ?? 'y1') as 'y1' | 'y2'),
          color: String(m['color'] ?? ''),
          symbol: String(m['symbol'] ?? 'circle'),
          symbolSize: m['symbolSize'] === undefined || m['symbolSize'] === '' ? 6 : Number(m['symbolSize']),
        }))
      : [];
    this.laSecondaryYField = '';
    const secondaryMeasure = this.laMeasures.find((m) => m.axis === 'y2');
    if (secondaryMeasure) this.laSecondaryYField = secondaryMeasure.field;
    this.laSecondaryXField = String(caps['secondaryXField'] ?? '');
    this.laSecondaryXValueField = toRawField(String(caps['secondaryXValueField'] ?? ''));
    this.laSecondaryXPosition = caps['secondaryXPosition'] === 'top' ? 'top' : 'bottom';
    const savedRefLines = Array.isArray(caps['referenceLines']) ? caps['referenceLines'] as Array<Record<string, unknown>> : [];
    this.laReferenceLines = savedRefLines.map((line) => {
      this.laRefLineIdCounter += 1;
      return {
        id: `la-refline-${this.laRefLineIdCounter}`,
        value: line['value'] == null ? null : Number(line['value']),
        label: String(line['label'] ?? ''),
        color: String(line['color'] ?? '#64748b'),
      };
    });
    this.host.barFormat = { ...(tile.format || {}) };
    if (this.host.drillDataset) this.host.loadColumnsForDataset(this.host.drillDataset);
  }

  reset(): void {
    this.laVariant = 'line';
    this.laCategory = '';
    this.laCategoryParent = '';
    this.laCategoryLabel = '';
    this.laYAxisLabel = '';
    this.laSecondaryYLabel = '';
    this.laSecondaryXLabel = '';
    this.laXAxisFontWeight = '';
    this.laYAxisFontWeight = '';
    this.laSecondaryXFontWeight = '';
    this.laSecondaryYFontWeight = '';
    this.laSeries = '';
    this.laSecondaryYField = '';
    this.laSecondaryXField = '';
    this.laSecondaryXValueField = '';
    this.laSecondaryXPosition = 'bottom';
    this.laMeasures = [];
    this.laReferenceLines = [];
  }

  /** A fresh Line & Area form starts with one empty measure row. */
  onLayoutActivated(): void {
    if (!this.laMeasures.length) this.addLaMeasure();
  }

  onFormatChanged(): void {
    this.refreshEditingLineAreaTile();
  }

  onDatasetRowsLoaded(): void {
    this.refreshEditingLineAreaTile();
  }

  /** Promotes the live preview tile (or adds a new one), then POSTs for server-computed rows. */
  generate(payload: Record<string, unknown>, url: string): boolean {
    this.host.isGenerating = false;
    if (this.host.previewTileId && this.host.dashboardTiles.some((t) => t.id === this.host.previewTileId)) {
      const id = this.host.previewTileId;
      this.host.dashboardTiles = this.host.dashboardTiles.map((t) =>
        t.id === id ? { ...this.rebuildLineAreaTile(t), payload } : t,
      );
      this.host.previewTileId = null;
    } else {
      const fresh = this.buildLineAreaTile(payload);
      const editing = this.host.editingTileId ? this.host.dashboardTiles.find((t) => t.id === this.host.editingTileId) : undefined;
      if (editing?.lineAreaData && isServerComputedClientTile(editing.lineAreaData as Record<string, unknown>, this.laCategory)) {
        fresh.lineAreaData = { ...(editing.lineAreaData as Record<string, unknown>), title: this.host.title.trim() || fresh.title };
        fresh.lineAreaRows = editing.lineAreaRows ?? null;
      }
      this.host.addTile(fresh);
    }
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        this.applyLineAreaComputeResponse(String(payload['componentId'] ?? ''), response);
        this.ui.showSuccess('Line / area chart added to the dashboard.');
      },
      error: () => this.ui.showError('Line / area chart added, but saving its configuration failed.'),
    });
    return true;
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.lineAreaData) return;
    const capabilities = this.applyLineAreaFormatToCapabilities({ ...(tile.lineAreaData['capabilities'] as unknown as LineAreaCapabilities) }, format);
    tile.lineAreaData = {
      ...tile.lineAreaData,
      title: (format['title'] as string) || tile.title,
      capabilities: capabilities as unknown as Record<string, unknown>,
    };
    tile.payload = {
      ...tile.payload,
      capabilities: capabilities as unknown as Record<string, unknown>,
    };
  }

  /** A server-computed envelope that fits the capabilities replaces the widget data; plain rows only when they fit the category field. */
  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    const envRows = extractLineAreaComputeRows(result['responseList']) ?? extractLineAreaComputeRows(result);
    if (envRows?.length && tile.lineAreaData
      && lineAreaRowsFitCapabilities(envRows, tile.lineAreaData['capabilities'] as Record<string, unknown> | undefined)) {
      tile.lineAreaData = buildLineAreaComputeWidgetData(result, {
        widgetId: 'dvm-tile-line-area-' + tile.id,
        title: tile.title,
        capabilities: (tile.lineAreaData['capabilities'] as Record<string, unknown> | undefined),
        format: tile.format,
      });
      tile.lineAreaRows = null; // server-computed → not client-filterable
      return true;
    }
    const rows = (Array.isArray(result['data'])
      ? result['data']
      : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
    if (tile.lineAreaData && this.host.rowsFitClientChart(rows, tile.lineAreaData, 'category')) {
      tile.lineAreaData = { ...tile.lineAreaData, staticData: Array.isArray(rows) ? rows : [] };
      return true;
    }
    return false;
  }
}
